<template>
  <div>
    <div class="row justify-content-between table-pre-header">
      <div class="stats">
        <template v-if="pagination.totalItems > 0">
          <button v-if="pagesCount > 1" :disabled="page === 1" class="nav-button btn btn-primary" @click.prevent="goToPrevious">&lt;</button>
          <button v-if="pagesCount > 1" :disabled="page === pagesCount" class="nav-button btn btn-primary" @click.prevent="goToNext">&gt;</button>
          Exports disponibles <b>{{ numStart }}</b> <template v-if="numStart !== numEnd">à <b>{{ numEnd }}</b></template> sur <b>{{ pagination.totalItems }}.</b>
        </template>
        <template v-else>
          Export des demandes de dispense.
        </template>
        <ButtonSpinner :is-loading="isLoading" />
      </div>
    </div>
    <div class="row">
      <div class="col afflex-table-content">
        <vuetable
            ref="vuetable"
            data-path="data"
            pagination-path="pagination"
            :http-fetch="fetchRequests"
            class="table table-striped table-bordered"
            no-data-template="Aucun export disponible"
            :fields="fields"
        >
          <template v-slot:status="props">
            <div v-if="props.rowData.status === 'ongoing'" class="status">
              <i class="fa fa-cog"></i> En cours de traitement
            </div>
            <div v-if="props.rowData.status === 'pending'" class="status">
              <i class="fa fa-clock"></i> En attente
            </div>
            <div v-else-if="props.rowData.status === 'completed'" class="status">
              <i class="fa fa-check"></i> Disponible
            </div>
            <div v-else-if="props.rowData.status === 'failed'" class="status">
              <i class="fa fa-exclamation-triangle"></i> En échec
            </div>
          </template>

          <template v-slot:actions="props">
            <div class="exports-list-buttons-wrapper">
              <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="props.rowData.status !== 'completed' || !props.rowData.fileSize || deletingIds[props.rowData.id]"
                  @click.prevent="downloadListExport(props.rowData.id)"
              >
                Ouvrir
              </button>
              <button
                  v-if="!deletingIds[props.rowData.id]"
                  type="button"
                  class="btn btn-primary"
                  @click.prevent="removeListExport(props.rowData.id)"
              >
                Supprimer
              </button>
              <span v-else class="spinner-border spinner-border-sm" role="status"></span>
            </div>
          </template>
        </vuetable>

        <Paginator
            v-if="pagesCount > 0"
            :pagination.sync="pagination"
            class="float-right"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Vuetable from 'vuetable-2';

import Paginator from '../../components/Paginator';
import PaginationMixin from '../../mixins/paginationMixin';
import { downloadExemptionsListExport, fetchListExemptionsExportRequests, removeExemptionsListExport } from '@/service/api/exemptions.api';
import ButtonSpinner from '@/components/ButtonSpinner.vue';
import { humanFileSize } from '@/utils/file';
import { errorMessage, messageTexts, successMessage } from '@/utils/sweet-alert-options';

export default {
  data() {
    return {
      requests: [],
      loadTimer: null,
      deletingIds: {},
      isLoading: false,
    };
  },
  mounted() {
    this.startQueryTimer();
  },
  beforeUnmount() {
    this.stopQueryTimer();
  },
  beforeDestroy() {
    this.stopQueryTimer();
  },
  computed: {
    fields() {
      return [
        {
          name: 'id',
          sortField: 'id',
          title: 'N°',
          width: '10%',
        },
        {
          name: 'status',
          sortField: 'status',
          title: 'Etat'
        },
        {
          name: 'filePath',
          sortField: 'filePath',
          title: 'Fichier',
        },
        {
          name: 'fileSize',
          sortField: 'fileSize',
          title: 'Taille',
          width: '10%',
          formatter: (fileSize) => fileSize ? humanFileSize(fileSize) : '.'
        },
        {
          name: 'createdAt',
          title: 'Date',
          sortField: 'exemption.createdAt',
          titleClass: 'text-center',
          dataClass: 'text-center',
          width: '15%',
          formatter: value => new Date(value).toLocaleString()
        },
        {
          name: 'actions',
          title: '',
          titleClass: 'text-center',
          dataClass: 'text-center',
          width: '20%'
        }
      ];
    }
  },
  watch: {
    page(page) {
      this.$refs.vuetable.gotoPage(page);
    }
  },
  methods: {
    async fetchRequests(_, { params: { page, per_page, sort } }) {
      this.fetchController && this.fetchController.abort();
      this.fetchController = new AbortController();
      const { signal } = this.fetchController;

      const loadingTimer = setTimeout(() => {
        this.isLoading = true;
      }, 400);

      try {
        const [field, direction] = sort.split('|');
        this.sortOrder = [{ field, direction }];

        const { data } = await fetchListExemptionsExportRequests(
            { currentPage: page, itemsPerPage: per_page },
            { field, direction },
            signal
        );

        const lastPage = Math.ceil(data.totalItems / per_page);
        if (page > lastPage && lastPage > 0) {
          this.$nextTick(() => {
            this.$refs.vuetable.gotoPage(lastPage);
          });

          return;
        }

        this.exemptions = data.items;
        this.pagination.currentPage = page;
        this.pagination.totalItems = data.totalItems;

        return {
          data: {
            data: data.items,
            pagination: {
              page,
              per_page: this.pagination.itemsPerPage,
              last_page: Math.ceil(data.totalItems / per_page)
            }
          }
        };
      } finally {
        clearTimeout(loadingTimer);
        this.isLoading = false;
      }
    },
    async downloadListExport(exportId) {
      try {
        await downloadExemptionsListExport(exportId);
      } catch (error) {
        if (error.response?.status === 404) {
          await removeExemptionsListExport(exportId).catch(() => {});
          this.$refs.vuetable.refresh();
          this.$swal({ title: 'Ce fichier n’est plus disponible et a été supprimé.', ...errorMessage });
        }
      }
    },
    async removeListExport(exportId) {
      this.$set(this.deletingIds, exportId, true);
      await removeExemptionsListExport(exportId);
      this.$swal({ title: messageTexts.exportListDeletionSubmitted, ...successMessage });
    },
    startQueryTimer(runImmediately = false) {
      runImmediately && this.$refs.vuetable.refresh();
      this.stopQueryTimer();
      this.loadTimer = setInterval(() => {
        this.$refs.vuetable.reload();
      }, 5000);
    },
    stopQueryTimer() {
      this.loadTimer && clearInterval(this.loadTimer);
    }
  },
  onIdle() {
    this.stopQueryTimer();
  },
  onActive() {
    this.startQueryTimer(true);
  },
  components: {
    ButtonSpinner,
    Vuetable,
    Paginator,
  },
  mixins: [PaginationMixin]
}
</script>

<style scoped lang="scss">
.content-wrapper {
  display: flex;

  /deep/ .pretty {
    width: 1.5em;
    margin-right: 0;

    &.p-default {
      input:checked ~ .state label:after {
        background-color: #007bff !important;
      }

      &.mixed {
        input:checked ~ .state label:after {
          background-color: darkgray !important;
        }
      }
    }

    .state {
      label:before, label:after {
        width: calc(1.5em + 2px);
        height: calc(1.5em + 2px);
      }
    }
  }

  .content {
    width: 100%;

    .table-pre-header {
      margin: 0 0 10px 0;
      display: flex;
      align-items: center;

      .stats {
        display: flex;
        column-gap: 5px;

        button.nav-button {
          padding: 0 5px;
        }
      }

      .button-wrapper {
        display: flex;
        column-gap: 10px;
      }

      .spinner-border {
        margin-top: 3px;
      }
    }

    .afflex-table-content {
      position: relative;

      .select-all {
        position: absolute;
        left: 22px;
        top: 15px;

        &.mixed {
          input:checked ~ .state label:after {
            background: lightgray !important;
          }
        }
      }
    }

    /deep/ .vuetable {
      .sort-icon.icon.chevron {
        display: inline-block;
        width: 0;
        height: 0;
        margin-left: 6px;
        vertical-align: middle;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 6px solid #007bff;
        opacity: 0.8;
      }

      .sort-icon.icon.chevron.down {
        transform: rotate(0deg);
      }

      .sort-icon.icon.chevron.up {
        transform: rotate(180deg);
      }

      .sort-icon.blue {
        border-top-color: #1e70bf; // correspond au bleu "Semantic UI"
      }

      .vuetable-body {
        button {
          padding: 0 20px;
        }
      }

      .center {
        text-align: center;
      }
    }

    .status > i {
      margin-right: 10px;
    }

    .exports-list-buttons-wrapper {
      display: flex;
      column-gap: 10px;
    }
  }
}
</style>
